import React, { useContext, useState } from 'react';
import IconButtonWhiteBg from '../../../common/IconButtonWhiteBg';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { AppContext } from '../../../../App';
import Delete from '@mui/icons-material/Delete';
import useDeleteExtraction from '../../../hooks/useDeleteExtraction';
import errorResponses from '../../../../errorResponses';
import { IExtraction } from '../../../../types/my-types';
import { useNavigate } from 'react-router';
import { getIdFromUrl } from '../../../../utils/getIdFromUrl';

interface IIconDeleteExtractionProps {
  extractionId?: string;
  extractions?: IExtraction[];
}

const IconDeleteExtraction: React.FC<IIconDeleteExtractionProps> = ({
  extractionId,
  extractions,
}) => {
  const { setToast, dataPoolId } = useContext(AppContext);

  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  if (!extractionId || !extractions) {
    return null;
  }

  const extractionBatchId = getIdFromUrl(extractions[0].extraction_batch);

  const { mutate: deleteExtraction, isPending: isLoading } = useDeleteExtraction({
    onSuccess: () => {
      // Forward user to next extraction if there is one
      const nextExtraction = extractions.find((extraction) => extraction.id !== extractionId);
      if (nextExtraction) {
        navigate(
          `/data-pools/${dataPoolId}/extraction-batch/${extractionBatchId}/review/${nextExtraction.id}`,
          { replace: true }
        );
        navigate(0);
      } else {
        // If no other extractions, redirect to the extraction batch details (new route)
        navigate(`/data-pools/${dataPoolId}/extraction-batch/${extractionBatchId}/details`);
      }

      setToast({
        variant: 'success',
        open: true,
        message: 'Extraction Deleted',
      });

      handleCloseConfirmation();
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToDeleteExtraction,
      });
    },
  });

  return (
    <>
      <IconButtonWhiteBg
        disabled={!!isLoading}
        onClick={handleClickOpen}
        id="icon-extraction-delete"
        cypressId="icon-extraction-delete"
        tooltip="Delete Extraction"
      >
        <Delete style={{ fontSize: '14px', color: 'black' }} />
      </IconButtonWhiteBg>
      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this extraction?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button
            onClick={() => deleteExtraction(extractionId)}
            autoFocus
            data-cy="btn-extraction-delete-modal-yes"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IconDeleteExtraction;
